import React, { useCallback, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import api from '../../api';
import { Sheet, SheetContent, SheetTitle } from '../ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { RequestThread, STATUS_CLS } from '../portal/RequestThread';
import { useToast } from '../../hooks/use-toast';

const Card = ({ r, focus, onChange }) => {
  useEffect(() => { if (r.unread) api.post(`/portal-requests/${r.id}/read`).catch(() => {}); }, [r.id, r.unread]);
  const { toast } = useToast();
  const setStatus = async (status) => {
    try { onChange((await api.post(`/portal-requests/${r.id}/status`, { status })).data); } catch { toast({ title: 'Could not update the status', variant: 'destructive' }); }
  };
  const reply = async (text) => onChange((await api.post(`/portal-requests/${r.id}/reply`, { text })).data);
  return (
    <div className={`rounded-xl border p-4 space-y-3 ${focus ? 'border-emerald-700 ring-2 ring-emerald-100' : 'border-stone-200'}`} data-testid={`firm-request-${r.id}`}>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-stone-900">{r.title}</div>
          <div className="text-xs text-stone-500 mt-0.5">{r.clientName} · {r.kind === 'task' ? 'Request' : 'Query'} · {new Date(r.createdAt).toLocaleDateString()}</div>
        </div>
        <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${STATUS_CLS[r.status]}`}>Client sees: {r.status}</span>
      </div>
      <Select value={r.taskStatus === 'Review' ? 'In Progress' : r.taskStatus} onValueChange={setStatus}>
        <SelectTrigger className="h-9 w-44" data-testid={`firm-request-status-${r.id}`}><SelectValue/></SelectTrigger>
        <SelectContent>{['To Do', 'In Progress', 'Done'].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
      </Select>
      <RequestThread req={r} side="firm" onSend={reply}/>
    </div>
  );
};

export const RequestsSheet = ({ open, onOpenChange, client, focusId, onChanged }) => {
  const [list, setList] = useState(null);
  const load = useCallback(() => api.get('/portal-requests', { params: client ? { clientId: client.id } : {} }).then((r) => setList(r.data)), [client]);
  useEffect(() => { if (open) { setList(null); load(); } }, [open, load]);
  useEffect(() => { if (list && focusId) document.querySelector(`[data-testid="firm-request-${focusId}"]`)?.scrollIntoView({ block: 'center' }); }, [list, focusId]);
  const change = (r) => { setList((l) => l.map((x) => (x.id === r.id ? r : x))); onChanged?.(); };
  return (
    <Sheet open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) onChanged?.(); }}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="requests-sheet">
        <SheetTitle>{client ? `${client.name} — questions & requests` : 'All portal questions & requests'}</SheetTitle>
        <p className="text-sm text-stone-500 mt-1">Each one is also on the Tasks board. Replies show instantly on the client's portal.</p>
        <div className="mt-5 space-y-4">
          {!list ? <div className="py-10 grid place-items-center"><Loader2 className="animate-spin text-stone-400"/></div>
            : list.length === 0 ? <div className="text-sm text-stone-500 text-center py-8" data-testid="requests-empty">Nothing from this client yet.</div>
            : list.map((r) => <Card key={r.id} r={r} focus={r.id === focusId} onChange={change}/>)}
        </div>
      </SheetContent>
    </Sheet>
  );
};
